import { useCallback, useMemo } from 'react'

import { SkeletonBox } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { useMoney } from '@circlefin/hooks'
import { useModal } from '@circlefin/modal-router'
import { useCurrentCurrency } from '@features/locales/hooks/currency'
import { useOnChainTransfer } from '@modals/blockchain'
import { routes } from '@services/sections/modal'
import { Currency } from '@shared/graphql'
import { useUsdcBalance } from '@shared/wallet/hooks/useUsdcBalance'
import useTranslation from 'next-translate/useTranslation'

import { ACE_MAXIMUM_DEPOSIT_LIMIT } from '../../constants'

import type { OnChainTransferDestination } from '@modals/blockchain'
import type { SupportedChainId } from '@shared/wallet/constants/chain'

const schema = y.object({
  /**
   * Transfer amount.
   */
  amount: y.number().required(),
})

type FormValues = y.InferType<typeof schema>

interface SelectAmountFormProps {
  /**
   * Wallet destination.
   */
  destination: OnChainTransferDestination
  /**
   * Destination Chain ID.
   */
  chainId: SupportedChainId
  /**
   * Destination Chain Name.
   */
  chainName: string
}

export const SelectAmountForm: React.FC<SelectAmountFormProps> = ({
  chainId,
  chainName,
  destination,
}) => {
  const { t } = useTranslation('ace')
  const { t: tCommon } = useTranslation('common')
  const { money } = useMoney()
  const [{ currency = Currency.USDC }] = useCurrentCurrency()
  const { router } = useModal()

  const [{ amount }, { setTransferState }] = useOnChainTransfer()

  const { data: availableBalance, isLoading } = useUsdcBalance(
    chainId,
    destination.address,
  )

  const isBalanceExceedingLimit = useMemo(
    () => Number(availableBalance) > ACE_MAXIMUM_DEPOSIT_LIMIT,
    [availableBalance],
  )

  const allowedAmount = useMemo(
    () =>
      isBalanceExceedingLimit
        ? ACE_MAXIMUM_DEPOSIT_LIMIT
        : Number(availableBalance),
    [availableBalance, isBalanceExceedingLimit],
  )

  const amountInputInfo = useMemo(() => {
    if (isBalanceExceedingLimit) {
      return t('deposit.selectAmount.depositLimit', {
        amount: money({
          number: ACE_MAXIMUM_DEPOSIT_LIMIT,
          variant: currency,
        }),
      })
    } else {
      return t('deposit.selectAmount.available', {
        amount: money({
          number: availableBalance,
          variant: currency,
        }),
        blockchain: chainName,
      })
    }
  }, [availableBalance, chainName, currency, isBalanceExceedingLimit, money, t])

  const [Form, { watch }] = useForm<FormValues>({
    schema: schema.shape({
      amount: y
        .number()
        .moreThan(0, {
          key: 'amount.moreThan',
          moreThan: money({
            number: 0,
            variant: currency,
          }),
        })
        .max(allowedAmount, {
          key: 'amount.max',
          max: money({
            number: allowedAmount,
            variant: currency,
          }),
        })
        .required()
        .allowEmpty(),
    }),
    defaultValues: {
      amount,
    },
  })

  const handleFormSubmit = useCallback(
    (values: FormValues) => {
      setTransferState({
        amount: values.amount,
      })

      router.push({
        pathname: routes.transfer.wallet.completeDeposit,
        query: {
          chainId,
        },
      })
    },
    [chainId, router, setTransferState],
  )

  const handleFormChange = useCallback(() => {
    const amount = Number(watch('amount'))

    setTransferState({
      amount,
    })
  }, [setTransferState, watch])

  return (
    <Form onChange={handleFormChange} onSubmit={handleFormSubmit}>
      <SkeletonBox className="h-20 w-full" loading={isLoading}>
        <Form.MoneyInput
          className="w-full"
          currencyVariant={currency}
          label={t('deposit.selectAmount.amount')}
          message={amountInputInfo}
          name="amount"
          padded
        />
      </SkeletonBox>

      <Form.SubmitButton className="mt-12 w-full" size="base" variant="primary">
        {tCommon('continue')}
      </Form.SubmitButton>
    </Form>
  )
}
